// Sources drawer (right, parchment). Resolves source ids via world.source(id):
// publisher, title, accessed date, link. Shows the focused source (a clicked chip), every
// source cited on screen, and the claim-label legend (truth rule 6).

import type { SourceId } from '../core/schema.ts';
import { useServices } from './context.ts';
import { STALE_YEARS, fmtDate } from './format.ts';
import { Icon } from './icons.tsx';
import { CLAIM_TEXT, ClaimLabel, type ClaimKind } from './parts.tsx';
import { onScreenSources, sourceFocus } from './store.ts';

const ON_SCREEN_MAX = 40;

const CLAIM_HELP: Record<ClaimKind, string> = {
  data: 'Measured or compiled statistics from a named dataset (World Bank, SIPRI, UNESCO …).',
  official: 'What a government or its officials say, quoted verbatim with date and venue.',
  assessment: 'Judgement by an independent body or research group, dated.',
  unverified: 'Reported but not confirmed by a second source. Drawn hatched, never as fact.',
};

const CLAIM_ORDER: ClaimKind[] = ['data', 'official', 'assessment', 'unverified'];

export function SourcesDrawer() {
  const { state, actions } = useServices();
  const panel = state.panel.value;
  const panelArg = state.panelArg.value;
  const focus = sourceFocus.value;
  const viaPanel = panel === 'sources' || panel === 'civilopedia';
  if (!viaPanel && focus.length === 0) {
    return null;
  }

  const focused = focus.length > 0 ? focus : panel === 'sources' && panelArg ? [panelArg] : [];
  const others = onScreenSources.value.filter((id) => !focused.includes(id)).slice(0, ON_SCREEN_MAX);

  const close = () => {
    sourceFocus.value = [];
    if (viaPanel) {
      actions.openPanel('none');
    }
  };

  return (
    <aside class="wc-sources wc-parchment" aria-label="Sources">
      <header class="wc-sources-head">
        <Icon name="source" size={24} />
        <div>
          <h2 class="wc-sources-title">Sources</h2>
          <div class="wc-sources-sub">Where every fact on screen comes from</div>
        </div>
        <button type="button" class="wc-ibtn wc-sources-x" aria-label="Close" onClick={close}>
          <Icon name="close" size={14} />
        </button>
      </header>

      <div class="wc-sources-body wc-scroll">
        {focused.length > 0 ? (
          <section>
            <h3 class="wc-rule-title">{focused.length > 1 ? 'Cited sources' : 'Cited source'}</h3>
            {focused.map((id) => (
              <SourceCard key={id} id={id} />
            ))}
          </section>
        ) : null}

        <section>
          <h3 class="wc-rule-title">Sources on screen</h3>
          {others.length === 0 ? <div class="wc-empty">No other sources are cited on screen.</div> : null}
          <ul class="wc-sources-list">
            {others.map((id) => (
              <SourceRow key={id} id={id} />
            ))}
          </ul>
        </section>

        <section>
          <h3 class="wc-rule-title">Reading the labels</h3>
          <dl class="wc-legend-claims">
            {CLAIM_ORDER.map((k) => (
              <div key={k}>
                <dt>
                  <ClaimLabel kind={k} />
                </dt>
                <dd>{CLAIM_HELP[k]}</dd>
              </div>
            ))}
            <div>
              <dt>
                <span class="wc-stale wc-num">12.3%</span> <span class="wc-year">(2019)</span>
              </dt>
              <dd>Greyed values are older than {STALE_YEARS} years at the date shown; the year is printed.</dd>
            </div>
          </dl>
          <p class="wc-sources-note">
            Every number, border, quote and marker must resolve to a source here. If it cannot, it is not drawn.
            Labels in this list: {CLAIM_ORDER.map((k) => CLAIM_TEXT[k]).join(' · ')}.
          </p>
        </section>
      </div>
    </aside>
  );
}

function SourceCard({ id }: { id: SourceId }) {
  const { world } = useServices();
  const s = world.source(id);
  if (!s) {
    return (
      <div class="wc-source-card wc-source-card--missing">
        <b>Unresolved source id</b>
        <code>{id}</code>
      </div>
    );
  }
  return (
    <div class="wc-source-card">
      <div class="wc-source-pub">{s.publisher}</div>
      <div class="wc-source-title">{s.title}</div>
      <div class="wc-source-meta">
        Accessed {fmtDate(s.accessed)} · <code>{id}</code>
      </div>
      <a class="wc-source-link" href={s.url} target="_blank" rel="noreferrer">
        Open source
        <Icon name="external" size={13} />
      </a>
    </div>
  );
}

function SourceRow({ id }: { id: SourceId }) {
  const { world } = useServices();
  const s = world.source(id);
  return (
    <li>
      <button
        type="button"
        class="wc-source-row"
        onClick={() => {
          sourceFocus.value = [id];
        }}
      >
        <b>{s?.publisher ?? 'Unresolved'}</b>
        <span>{s?.title ?? id}</span>
      </button>
    </li>
  );
}
